/**
 * @component ry-radio-group
 * @category  form
 * @summary   Accessible wrapper for a set of `<ry-radio>` children. Provides `role="radiogroup"`, optional label, and WAI-ARIA arrow-key navigation (Left/Up = previous, Right/Down = next, with wrap and skipping disabled). The radios still own their state and form-submission semantics — the group is just an a11y + keyboard layer.
 *
 * @attribute label  string?   Optional ARIA label for the radiogroup. Becomes `aria-label`. If you have a visible label element, prefer wrapping in `<ry-field label="…">` and skipping this attribute.
 *
 * @keyboard ArrowLeft / ArrowUp    — focus + check previous enabled radio (wraps)
 *           ArrowRight / ArrowDown — focus + check next enabled radio (wraps)
 *           Home                    — first enabled radio
 *           End                     — last enabled radio
 *
 * @content  One or more `<ry-radio>` children. Non-radio children are ignored by
 *           the keyboard handler but pass through layout.
 *
 * @dom
 *   ry-radio-group [role="radiogroup"] [aria-label?]
 *     > ry-radio ...
 *     > ry-radio ...
 *
 * @gotcha The group does NOT enforce a shared `name`. Set `name` on each
 *         `<ry-radio>` child for actual radio-group semantics (browser
 *         auto-uncheck + form submission). The group is the a11y wrapper, not the
 *         state owner.
 *
 * @example basic
 * ```html
 * <ry-radio-group label="Plan">
 *   <ry-radio name="plan" value="free" checked>Free</ry-radio>
 *   <ry-radio name="plan" value="pro">Pro</ry-radio>
 *   <ry-radio name="plan" value="ent">Enterprise</ry-radio>
 * </ry-radio-group>
 * ```
 *
 * @example with-disabled-skip
 * ```html
 * <ry-radio-group label="Shipping">
 *   <ry-radio name="ship" value="std" checked>Standard</ry-radio>
 *   <ry-radio name="ship" value="exp" disabled>Express (sold out)</ry-radio>
 *   <ry-radio name="ship" value="ovr">Overnight</ry-radio>
 * </ry-radio-group>
 * ```
 *
 * @tutorial role-and-label
 * ```js
 * const el = mount('<ry-radio-group label="Plan"><ry-radio name="p" value="a">A</ry-radio></ry-radio-group>');
 * await expectAttr(el, 'role', 'radiogroup');
 * await expectAttr(el, 'aria-label', 'Plan');
 * ```
 *
 * @tutorial arrow-keys-navigate-and-check
 * ```js
 * const el = mount(`
 *   <ry-radio-group label="x">
 *     <ry-radio id="r1" name="x" value="a" checked>A</ry-radio>
 *     <ry-radio id="r2" name="x" value="b">B</ry-radio>
 *     <ry-radio id="r3" name="x" value="c">C</ry-radio>
 *   </ry-radio-group>
 * `);
 * const r1 = el.querySelector('#r1');
 * const r2 = el.querySelector('#r2');
 * // Arrow keys are handled on the group; dispatch from the focused radio.
 * r1.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(r2.checked, true, 'next radio checked');
 * await expectEqual(r1.checked, false, 'previous radio unchecked by group');
 * ```
 *
 * @see ry-radio, ry-checkbox, ry-field
 */

import { RyElement } from '../core/ry-element.js';
import type { RyRadio } from './ry-radio.js';

export class RyRadioGroup extends RyElement {
  static observedAttributes = ['label'] as const;

  // The aria-label we've written (so we don't clobber an explicit consumer value).
  #managedAriaLabel: string | null = null;

  setup(): void {
    this.setAttribute('role', 'radiogroup');
    const label = this.getAttribute('label');
    if (label && !this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', label);
      this.#managedAriaLabel = label;
    }
    this.on(this, 'keydown', this.#handleKeydown);
  }

  attributeChangedCallback(name: string, _old: string | null, value: string | null): void {
    if (name === 'label') {
      // Only sync after setup() has run; otherwise we may clobber an explicit
      // aria-label that the consumer set at mount time (setup captures whether
      // we "own" the aria-label and stores it as managed).
      if (!this.isConnected) return;
      const currentAria = this.getAttribute('aria-label');
      // If aria-label is present and we didn't set it, leave it alone.
      if (currentAria !== null && currentAria !== this.#managedAriaLabel) return;

      if (value === null) {
        this.removeAttribute('aria-label');
        this.#managedAriaLabel = null;
      } else {
        this.setAttribute('aria-label', value);
        this.#managedAriaLabel = value;
      }
    }
  }

  #handleKeydown = (event: KeyboardEvent): void => {
    const key = event.key;
    if (
      key !== 'ArrowRight' &&
      key !== 'ArrowDown' &&
      key !== 'ArrowLeft' &&
      key !== 'ArrowUp' &&
      key !== 'Home' &&
      key !== 'End'
    ) return;

    const radios = this.#enabledRadios();
    if (radios.length === 0) return;

    // Find the currently-focused radio (host or its inner input). Fall back to
    // the currently-checked radio.
    const active = document.activeElement;
    let currentIdx = radios.findIndex(
      r => r === active || r.contains(active as Node)
    );
    if (currentIdx === -1) currentIdx = radios.findIndex(r => r.checked);
    if (currentIdx === -1) currentIdx = 0;

    let nextIdx = currentIdx;
    if (key === 'ArrowRight' || key === 'ArrowDown') {
      nextIdx = (currentIdx + 1) % radios.length;
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      nextIdx = (currentIdx - 1 + radios.length) % radios.length;
    } else if (key === 'Home') {
      nextIdx = 0;
    } else if (key === 'End') {
      nextIdx = radios.length - 1;
    }

    if (nextIdx === currentIdx) return;

    event.preventDefault();
    const next = radios[nextIdx];
    if (!next) return;

    // Drive the inner input to fire the native change event flow — this triggers
    // ry-radio's own #handleChange (sibling uncheck + ry:change emission).
    const input = next.querySelector<HTMLInputElement>('input[type="radio"]');
    if (!input) return;
    input.checked = true;
    input.focus();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #enabledRadios(): RyRadio[] {
    return this.$$<RyRadio>('ry-radio').filter(r => !r.hasAttribute('disabled'));
  }
}

customElements.define('ry-radio-group', RyRadioGroup);
